// src/pages/PaperUnfolding.js
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import TutorialWindow from "../components/TutorialWindow";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { paperUnfoldingTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import "../styles/common.css";

// 简单的API配置
//const API_URL = process.env.REACT_APP_API_URL || "https://paper-unfold-backend-611330231833.us-central1.run.app";

const API_URL = "https://paper-unfold-backend-611330231833.us-central1.run.app";

const PaperUnfolding = ({ stlFile }) => {
  const containerRef = useRef(null);

  // Three.js objects
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);

  const [faceCountError, setFaceCountError] = useState(null);
  const [faceCount, setFaceCount] = useState(0);

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [processingUnfold, setProcessingUnfold] = useState(false);
  const [svgFiles, setSvgFiles] = useState([]);
  const [svgPreviewVisible, setSvgPreviewVisible] = useState(false);
  const [printNumbers, setPrintNumbers] = useState(true);
  const fastIterRecommended = useMemo(() => true, []);
  const pageId = useMemo(() => `paper-unfolding`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // Helper hooks
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();

  const checkFaceCount = useCallback((geometry) => {
    const MAX_FACES = 100;
    let faces = 0;

    if (geometry.index) {
      faces = geometry.index.count / 3;
    } else {
      faces = geometry.attributes.position.count / 3;
    }

    setFaceCount(Math.floor(faces));

    if (faces > MAX_FACES) {
      setFaceCountError(
        `The model has too many faces (${Math.floor(
          faces
        )} faces), exceeding the maximum limit of ${MAX_FACES} faces.`
      );
      return false;
    } else {
      setFaceCountError(null);
      return true;
    }
  }, []);

  // Materials
  const materials = React.useMemo(
    () => ({
      paperMaterial: new THREE.MeshStandardMaterial({
        color: 0xf5f5dc,
        roughness: 0.8,
        metalness: 0.0,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
      }),
    }),
    []
  );

  // Initialize Three.js scene
  const initScene = useCallback(() => {
    if (!containerRef.current) return;

    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    const handleResize = () => {
      if (cameraRef.current && rendererRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (cleanup) cleanup();
    };
  }, []);

  // 转换几何体为STL格式
  const geometryToSTL = (geometry) => {
    const vertices = geometry.attributes.position.array;
    const faces = geometry.index ? geometry.index.array : null;

    let stlString = "solid exported\n";

    if (faces) {
      // 有索引的几何体
      for (let i = 0; i < faces.length; i += 3) {
        const a = faces[i] * 3;
        const b = faces[i + 1] * 3;
        const c = faces[i + 2] * 3;

        // 计算法向量
        const v1 = new THREE.Vector3(
          vertices[a],
          vertices[a + 1],
          vertices[a + 2]
        );
        const v2 = new THREE.Vector3(
          vertices[b],
          vertices[b + 1],
          vertices[b + 2]
        );
        const v3 = new THREE.Vector3(
          vertices[c],
          vertices[c + 1],
          vertices[c + 2]
        );

        const normal = new THREE.Vector3()
          .crossVectors(
            new THREE.Vector3().subVectors(v2, v1),
            new THREE.Vector3().subVectors(v3, v1)
          )
          .normalize();

        stlString += `  facet normal ${normal.x} ${normal.y} ${normal.z}\n`;
        stlString += `    outer loop\n`;
        stlString += `      vertex ${v1.x} ${v1.y} ${v1.z}\n`;
        stlString += `      vertex ${v2.x} ${v2.y} ${v2.z}\n`;
        stlString += `      vertex ${v3.x} ${v3.y} ${v3.z}\n`;
        stlString += `    endloop\n`;
        stlString += `  endfacet\n`;
      }
    } else {
      // 无索引的几何体
      for (let i = 0; i < vertices.length; i += 9) {
        const v1 = new THREE.Vector3(
          vertices[i],
          vertices[i + 1],
          vertices[i + 2]
        );
        const v2 = new THREE.Vector3(
          vertices[i + 3],
          vertices[i + 4],
          vertices[i + 5]
        );
        const v3 = new THREE.Vector3(
          vertices[i + 6],
          vertices[i + 7],
          vertices[i + 8]
        );

        const normal = new THREE.Vector3()
          .crossVectors(
            new THREE.Vector3().subVectors(v2, v1),
            new THREE.Vector3().subVectors(v3, v1)
          )
          .normalize();

        stlString += `  facet normal ${normal.x} ${normal.y} ${normal.z}\n`;
        stlString += `    outer loop\n`;
        stlString += `      vertex ${v1.x} ${v1.y} ${v1.z}\n`;
        stlString += `      vertex ${v2.x} ${v2.y} ${v2.z}\n`;
        stlString += `      vertex ${v3.x} ${v3.y} ${v3.z}\n`;
        stlString += `    endloop\n`;
        stlString += `  endfacet\n`;
      }
    }

    stlString += "endsolid exported\n";
    return stlString;
  };

  // 处理展开
  const processUnfolding = useCallback(async () => {
    if (!objRef.current) return;

    setProcessingUnfold(true);
    setError(null);

    try {
      console.log("Starting paper unfolding...");

      // 将当前模型转换为STL
      const geometry = objRef.current.geometry;
      const stlContent = geometryToSTL(geometry);

      // 创建File对象
      const stlBlob = new Blob([stlContent], { type: "text/plain" });
      const stlFile = new File([stlBlob], "model.stl", { type: "text/plain" });

      // 发送到后端
      const formData = new FormData();
      formData.append("file", stlFile);
      formData.append("printNumbers", printNumbers.toString());

      console.log("Sending STL to backend...");
      const response = await fetch(`${API_URL}/unfold_file`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log("Received SVG files:", result);

      if (result.success && result.svg_files) {
        setSvgFiles(result.svg_files);
        setSvgPreviewVisible(true);
        console.log(`Generated ${result.svg_files.length} SVG templates`);
      } else {
        throw new Error("No SVG files generated");
      }
    } catch (error) {
      console.error("Error during unfolding:", error);
      setError("Failed to process paper unfolding: " + error.message);
    } finally {
      setProcessingUnfold(false);
    }
  }, [objRef, printNumbers]);

  // 导出SVG函数
  const exportSVG = useCallback(() => {
    if (!svgFiles || svgFiles.length === 0) {
      alert("Please process the unfolding first!");
      return;
    }

    svgFiles.forEach((svgFile, index) => {
      setTimeout(() => {
        const blob = new Blob([svgFile.content], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = svgFile.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      }, index * 200);
    });

    setTutorialVisible(true);
  }, [svgFiles]);

  // SVG预览组件
  const SVGPreview = ({ svgFiles, visible, onClose }) => {
    if (!visible || !svgFiles || svgFiles.length === 0) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.8)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "white",
            border: "2px solid #ccc",
            borderRadius: "8px",
            padding: "20px",
            maxWidth: "90%",
            maxHeight: "90%",
            overflow: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h3>📄 Paper Templates ({svgFiles.length} files)</h3>
            <button
              onClick={onClose}
              style={{
                background: "#ff4444",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            {svgFiles.map((svgFile, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "15px",
                }}
              >
                <h4>{svgFile.filename}</h4>
                <div
                  dangerouslySetInnerHTML={{ __html: svgFile.content }}
                  style={{
                    width: "100%",
                    height: "300px",
                    overflow: "auto",
                    border: "1px solid #eee",
                  }}
                />
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center" }}>
            <p>
              🔴 Red lines: Mountain folds | 🔵 Blue lines: Valley folds | ⚫
              Black lines: Cut lines
            </p>
            <button
              onClick={exportSVG}
              style={{
                background: "#3288e6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                padding: "12px 24px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              📥 Download All SVG Files
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Load STL model
  const loadModel = useCallback(
    (filePath) => {
      setIsLoading(true);
      setError(null);

      if (objRef.current) {
        sceneRef.current?.remove(objRef.current);
        objRef.current.geometry?.dispose();
        objRef.current.material?.dispose();
        objRef.current = null;
      }

      removeVisualHelpers(sceneRef.current);

      const loader = new STLLoader();
      const path = filePath || "/models/default.stl";

      loader.load(
        path,
        (geometry) => {
          try {
            geometry.computeBoundingBox();
            const originalBoundingBox = geometry.boundingBox.clone();

            geometry.translate(
              -originalBoundingBox.getCenter(new THREE.Vector3()).x,
              -originalBoundingBox.getCenter(new THREE.Vector3()).y,
              -originalBoundingBox.getCenter(new THREE.Vector3()).z
            );
            geometry.rotateX(-Math.PI / 2);
            geometry.rotateY(-Math.PI / 2);

            // 检查面数
            const isValidFaceCount = checkFaceCount(geometry);
            if (!isValidFaceCount) {
              setIsLoading(false);
              return; // 超过限制就直接停止，不 add 到 scene
            }

            const mesh = new THREE.Mesh(geometry, materials.paperMaterial);
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            const boundingBox = new THREE.Box3().setFromObject(mesh);
            const size = boundingBox.getSize(new THREE.Vector3());

            mesh.position.y -= boundingBox.min.y;
            mesh.updateMatrix();
            mesh.updateMatrixWorld(true);

            objRef.current = mesh;
            sceneRef.current.add(mesh);

            setModelDimensions({
              width: size.x,
              height: size.y,
              depth: size.z,
            });

            const updatedBoundingBox = new THREE.Box3().setFromObject(mesh);
            createVisualization(mesh, sceneRef.current, updatedBoundingBox);

            setIsLoading(false);
          } catch (error) {
            setError("Failed to process 3D model");
            setIsLoading(false);
          }
        },
        undefined,
        (error) => {
          setError("Failed to load 3D model");
          setIsLoading(false);
        }
      );
    },
    [
      sceneRef,
      removeVisualHelpers,
      materials.paperMaterial,
      createVisualization,
      checkFaceCount,
    ]
  );

  useEffect(() => {
    const cleanup = initScene();
    return () => {
      if (cleanup) cleanup();
    };
  }, [initScene]);

  useEffect(() => {
    if (stlFile && sceneRef.current) {
      loadModel(stlFile);
    }
  }, [stlFile, loadModel]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container" />

      {faceCountError && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1001,
            backgroundColor: "#ff4444",
            color: "white",
            padding: "12px 20px",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
            maxWidth: "90vw",
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: "20px", marginRight: "10px" }}>⚠️</span>
            <div>
              <strong>Model Face Count Exceeded</strong>
              <br />
              {faceCountError}
            </div>
            <button
              onClick={() => setFaceCountError(null)}
              style={{
                marginLeft: "15px",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="panel-toggle-button"
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {!isLoading && expanded && (
        <div className={`controls-panel ${panelCollapsed ? "collapsed" : ""}`}>
          <div className="panel-title-row">
            <div
              className="panel-title"
              onClick={() => setPanelCollapsed(!panelCollapsed)}
            >
              Paper Unfolding
              <span className="collapse-icon">
                {panelCollapsed ? "▼" : "▲"}
              </span>
            </div>

            {fastIterRecommended && (
              <span
                className="iteration-badge badge--ok"
                title="Low-cost + printable templates = great for quick iteration"
              >
                Rapid prototyping friendly
              </span>
            )}
          </div>

          {!panelCollapsed && (
            <>
              <div className="panel-section">
                <div className="section-header">
                  <h4>Paper Template Generator</h4>
                </div>
                <div className="section-content">
                  <p>
                    Convert your 3D model into printable paper templates. The
                    system will create flat patterns that you can cut and fold
                    to recreate your model.
                  </p>
                </div>
              </div>

              <div className="panel-section">
                <div className="section-header">
                  <h4>Time & Effort</h4>
                </div>
                <div className="section-content">
                  <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
                    <li>
                      <strong>Template prep</strong>: auto-generate SVGs, print
                      on paper or card.
                    </li>
                    <li>
                      <strong>Cutting</strong>: scissors/knife along cut lines;
                      quick and low-cost.
                    </li>
                    <li>
                      <strong>Scoring & folding</strong>: light score on fold
                      lines for crisp bends.
                    </li>
                    <li>
                      <strong>Assembly</strong>: glue or tape tabs; iterate
                      shape in minutes.
                    </li>
                  </ul>
                  <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
                    Tip: start small to verify folds/numbers, then scale up once
                    satisfied.
                  </p>
                </div>
              </div>

              <div className="panel-section">
                <div className="section-content">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={printNumbers}
                      onChange={(e) => setPrintNumbers(e.target.checked)}
                    />
                    Print edge numbers for assembly
                  </label>
                </div>
              </div>

              <div className="buttons-container">
                <button
                  onClick={processUnfolding}
                  className="action-button"
                  disabled={
                    processingUnfold || !objRef.current || !!faceCountError
                  } // 加 faceCountError
                >
                  {processingUnfold ? "🔄 PROCESSING..." : "GENERATE TEMPLATES"}
                </button>

                <button
                  onClick={exportSVG}
                  className="action-button"
                  disabled={!svgFiles || svgFiles.length === 0}
                >
                  DOWNLOAD SVG FILES
                </button>

                <button
                  onClick={() => setSvgPreviewVisible(!svgPreviewVisible)}
                  className="action-button"
                  disabled={!svgFiles || svgFiles.length === 0}
                >
                  {svgPreviewVisible ? " HIDE PREVIEW" : " SHOW PREVIEW"}
                </button>

                <button
                  onClick={() => setTutorialVisible(!tutorialVisible)}
                  className="action-button"
                >
                  SHOW TUTORIAL
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {isLoading && <div className="loading-box">🔄 Loading model...</div>}
      {error && <div className="error-box">❌ Error: {error}</div>}
      {processingUnfold && (
        <div className="loading-box">🔄 Processing unfolding...</div>
      )}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(!tutorialVisible)}
        tutorialContent={paperUnfoldingTutorial}
      />

      <SVGPreview
        svgFiles={svgFiles}
        visible={svgPreviewVisible}
        onClose={() => setSvgPreviewVisible(false)}
      />
    </div>
  );
};

export default PaperUnfolding;
